/**
 * JumpstartStatusBlock — Accelerated Jumpstart milestone track.
 *
 * Shows Day 30, Day 60, and Day 90 milestones relative to an engagement start
 * date. Color-coded per the 5-color palette (canon §3):
 *   gray #6B7280   upcoming (>7 days out)
 *   yellow #EAB308 in_window (within 7 days before/after)
 *   green #16A34A  hit (milestone day + 7 days passed)
 *   red #DC2626    missed (hit_at is null and current_day > milestone_day + 14)
 *
 * No exclamation points. No emojis.
 */

import React from "react";

// ── Palette ──────────────────────────────────────────────────────────────────
const GRAY   = "#6B7280";
const YELLOW = "#EAB308";
const GREEN  = "#16A34A";
const RED    = "#DC2626";

type MilestoneState = "upcoming" | "in_window" | "hit" | "missed";

interface MilestoneHit {
  day: number;
  hit_at?: string | null; // ISO string if milestone was confirmed hit
}

interface JumpstartStatusBlockProps {
  /** ISO date string or Date object for engagement start */
  engagement_start_date: string | Date;
  /** Optional account ID for display context */
  account_id?: string | number;
  /** Optional per-milestone hit confirmations (from server if available) */
  milestoneHits?: MilestoneHit[];
}

function getMilestoneState(
  currentDay: number,
  milestoneDay: number,
  hit_at?: string | null
): MilestoneState {
  // Confirmed hit
  if (hit_at) return "hit";
  // Missed: no confirmation and well past the window
  if (currentDay > milestoneDay + 14) return "missed";
  // Hit (assumed): past the 7-day-after grace window with no data
  if (currentDay >= milestoneDay + 7) return "hit";
  // In window: within 7 days before through 7 days after
  if (currentDay >= milestoneDay - 7) return "in_window";
  // Upcoming
  return "upcoming";
}

function milestoneColor(state: MilestoneState): string {
  switch (state) {
    case "hit":       return GREEN;
    case "missed":    return RED;
    case "in_window": return YELLOW;
    default:          return GRAY;
  }
}

function milestoneLabel(state: MilestoneState): string {
  switch (state) {
    case "hit":       return "Hit";
    case "missed":    return "Missed";
    case "in_window": return "In window";
    default:          return "Upcoming";
  }
}

export function JumpstartStatusBlock({
  engagement_start_date,
  account_id,
  milestoneHits,
}: JumpstartStatusBlockProps) {
  const startDate = engagement_start_date instanceof Date
    ? engagement_start_date
    : new Date(engagement_start_date);

  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const rawDiff = Math.floor((now.getTime() - startDate.getTime()) / msPerDay);
  const currentDay = Math.max(0, rawDiff);

  const MILESTONES = [30, 60, 90] as const;

  const getHitAt = (day: number): string | null | undefined => {
    if (!milestoneHits) return undefined;
    return milestoneHits.find((h) => h.day === day)?.hit_at;
  };

  const milestones = MILESTONES.map((day) => {
    const hit_at = getHitAt(day);
    const state = getMilestoneState(currentDay, day, hit_at);
    return { day, state, color: milestoneColor(state), label: milestoneLabel(state) };
  });

  const startDateStr = startDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Progress along the 90-day track as a percentage (clamped 0–100)
  const progressPct = Math.min(100, Math.max(0, (currentDay / 90) * 100));

  return (
    <div
      className="rounded-lg border"
      style={{
        background: "#FFFFFF",
        borderColor: "#E5E7EB",
        padding: "16px 20px",
        marginBottom: "16px",
      }}
    >
      {/* Header row */}
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 12 }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: GRAY,
          }}
        >
          Accelerated Jumpstart
        </span>
        {account_id != null && (
          <span style={{ fontSize: 11, color: GRAY }}>
            Account {account_id}
          </span>
        )}
      </div>

      {/* Track */}
      <div
        style={{
          position: "relative",
          height: 6,
          background: "#F3F4F6",
          borderRadius: 3,
          marginBottom: 24,
        }}
      >
        {/* Progress fill */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progressPct}%`,
            background: milestones[milestones.length - 1].color,
            borderRadius: 3,
            transition: "width 0.3s ease",
          }}
        />

        {/* Milestone dots */}
        {milestones.map(({ day, color, state, label }) => {
          const pct = (day / 90) * 100;
          return (
            <div
              key={day}
              style={{
                position: "absolute",
                left: `${pct}%`,
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              {/* Dot */}
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: color,
                  border: "2px solid #FFFFFF",
                  boxShadow: "0 0 0 1px " + color,
                }}
              />
              {/* Label below dot */}
              <div
                style={{
                  position: "absolute",
                  top: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  whiteSpace: "nowrap",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color,
                  }}
                >
                  Day {day}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: GRAY,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer row */}
      <div
        className="flex items-center justify-between"
        style={{ marginTop: 36 }}
      >
        <span style={{ fontSize: 12, color: GRAY }}>
          Start:{" "}
          <code
            style={{
              fontFamily: "ui-monospace, 'Cascadia Code', 'Fira Mono', monospace",
              fontSize: 11,
              color: "#374151",
            }}
          >
            {startDateStr}
          </code>
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
          }}
        >
          Day{" "}
          <span
            style={{
              display: "inline-block",
              background: "#F3F4F6",
              border: "1px solid #E5E7EB",
              borderRadius: 4,
              padding: "1px 6px",
              fontFamily: "ui-monospace, 'Cascadia Code', 'Fira Mono', monospace",
              fontSize: 12,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            {currentDay}
          </span>
        </span>
      </div>
    </div>
  );
}
